import apiClient from '../client/axios';
import { API_CONFIG, API_ENDPOINTS } from '../config';
import { mockAuthAPI } from '../client/mock';
import { User } from 'models/user.model';

export interface LoginCredentials {
  cellphone: string;
  password?: string;
  otpCode?: string;
}

interface RegisterData {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  email: string;
  cellphone: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export interface RegisterResponse {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  cellphone: string;
  isActive: boolean;
  roles: User['roles'];
  createdAt: string;
  lastLoginAt: string | null;
}

export interface LogoutResponse {
  message: string;
}

const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  if (API_CONFIG.USE_MOCK) {
    if (credentials.otpCode) {
      const mock = await mockAuthAPI.login({ email: 'admin@timax.com', password: 'admin123' });
      if (!mock.success || !mock.data) throw new Error(mock.message ?? 'Login failed');
      return { accessToken: mock.data.accessToken, refreshToken: mock.data.refreshToken, user: mock.data.user as User };
    }

    const mock = await mockAuthAPI.login({
      email: credentials.cellphone,
      password: credentials.password ?? ''
    });
    if (!mock.success || !mock.data) throw new Error(mock.message ?? 'Login failed');
    return { accessToken: mock.data.accessToken, refreshToken: mock.data.refreshToken, user: mock.data.user as User };
  }

  const payload: Record<string, string> = { cellphone: credentials.cellphone };
  if (credentials.password) payload.password = credentials.password;
  if (credentials.otpCode) payload.otpCode = credentials.otpCode;

  return apiClient.post<unknown, LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, payload);
};

const requestOtp = async (cellphone: string): Promise<void> => {
  if (API_CONFIG.USE_MOCK) {
    return;
  }

  await apiClient.post(API_ENDPOINTS.AUTH.REQUEST_OTP, { cellphone });
};

const register = async (userData: RegisterData): Promise<RegisterResponse> => {
  if (API_CONFIG.USE_MOCK) {
    const mock = await mockAuthAPI.register({ email: userData.email, password: userData.password, name: `${userData.firstName} ${userData.lastName}` });
    if (!mock.success || !mock.data) throw new Error(mock.message ?? 'Registration failed');
    return mock.data.user as unknown as RegisterResponse;
  }

  return apiClient.post<unknown, RegisterResponse>(API_ENDPOINTS.AUTH.REGISTER, userData);
};

const refresh = async (refreshToken: string): Promise<LoginResponse> => {
  if (API_CONFIG.USE_MOCK) {
    const mock = await mockAuthAPI.refresh(refreshToken);
    if (!mock.success || !mock.data) throw new Error('Refresh failed');
    const loginMock = await mockAuthAPI.login({ email: 'admin@timax.com', password: 'admin123' });
    if (!loginMock.success || !loginMock.data) throw new Error('Refresh failed');
    return { accessToken: mock.data.accessToken, refreshToken, user: loginMock.data.user as User };
  }

  return apiClient.post<unknown, LoginResponse>(API_ENDPOINTS.AUTH.REFRESH, { refreshToken });
};

const logout = async (userId: string): Promise<LogoutResponse> => {
  if (API_CONFIG.USE_MOCK) {
    return { message: 'Logged out successfully' };
  }

  return apiClient.post<unknown, LogoutResponse>(API_ENDPOINTS.AUTH.LOGOUT(userId));
};

const authService = {
  login,
  requestOtp,
  register,
  refresh,
  logout
};

export default authService;
